<script lang="ts">
/** Create or edit — the modal's two jobs, and the two label sets it needs. */
export type GaiaClauseFormMode = 'create' | 'edit';

/** Whichever shape of clause a caller happens to hold. */
export type GaiaEditableClause = ClauseInDb | GaiaClauseInDb | ClauseSlim;
</script>

<script setup lang="ts">
import { computed, reactive, ref, watch, type PropType } from 'vue';
import type { SelectOption } from 'naive-ui';
import {
  ClauseCategory,
  type ClauseInDb,
  type ClauseSlim,
  type ContractTypeRef,
  ContractTypeTemplateKey,
  type GaiaClauseInDb,
} from '@/api';
import {
  ClauseLibraryService,
  type ClauseCreateWrite,
  type ClauseLibrary,
  type LibraryClause,
} from '@/services/clauseLibraryService';
import { useContractTypeStore } from '@/stores/contractTypeStore';
import { isEnumValue } from '@/utils/enumUtils';
import { logger } from '@/utils/logger';
import GaiaButton from '../../base/GaiaButton.vue';
import GaiaFormField from '../../base/GaiaFormField.vue';
import GaiaHeader from '../../base/GaiaHeader.vue';
import GaiaInput from '../../base/inputs/GaiaInput.vue';
import GaiaText from '../../base/GaiaText.vue';
import GaiaTextarea from '../../base/inputs/GaiaTextarea.vue';
import GaiaEnumSelect from '../../base/selects/GaiaEnumSelect.vue';
import GaiaSelect from '../../base/selects/GaiaSelect.vue';
import GaiaModal from '../../base/GaiaModal.vue';
import GaiaBanner from '../../composite/GaiaBanner.vue';
import { clauseCategoryMap, contractTypeTemplateKeyMap } from '../../statusMaps';
import { gaiaFonts } from '../../themes/tokens';
import { useGaiaToast } from '@/composables/useGaiaToast';

const props = defineProps({
  show: {
    type: Boolean,
    required: true,
  },
  mode: {
    type: String as PropType<GaiaClauseFormMode>,
    default: 'edit',
  },
  /** Which library the save lands in — the only thing that differs per tab. */
  library: {
    type: String as PropType<ClauseLibrary>,
    default: 'organisation',
  },
  /** The clause being edited. Ignored in create mode. */
  clause: {
    type: Object as PropType<GaiaEditableClause | null>,
    default: null,
  },
  /**
   * Fields to open a *create* form with. This is how "Copy to organisation"
   * works: a GAIA clause's wording, editable before it becomes the
   * organisation's own — a copy nobody could review before saving would be a
   * worse feature than none.
   */
  prefill: {
    type: Object as PropType<GaiaEditableClause | null>,
    default: null,
  },
  /** Overrides the derived title, e.g. "Copy to organisation". */
  heading: {
    type: String,
    default: undefined,
  },
});

const emit = defineEmits<{
  'update:show': [value: boolean];
  saved: [clause: LibraryClause];
}>();

const message = useGaiaToast();
const contractTypeStore = useContractTypeStore();

interface ClauseFormData {
  name: string;
  jurisdiction: string;
  language: string;
  /** The organisation's type uids, or template keys in the GAIA library. */
  contract_types: string[];
  clause_category: ClauseCategory;
  content: string;
  source: string | null;
}

/** Which required fields the user has visited, so an error only shows once it is earned. */
type TouchableField = 'name' | 'contract_types' | 'jurisdiction' | 'language' | 'content';

function emptyForm(): ClauseFormData {
  return {
    name: '',
    jurisdiction: '',
    language: '',
    contract_types: [],
    clause_category: ClauseCategory.OTHER,
    content: '',
    source: null,
  };
}

const form = reactive<ClauseFormData>(emptyForm());
const touched = reactive<Record<TouchableField, boolean>>({
  name: false,
  contract_types: false,
  jurisdiction: false,
  language: false,
  content: false,
});
const saving = ref(false);
const saveError = ref<string | null>(null);

const isCreate = computed(() => props.mode === 'create');

const title = computed(() => {
  if (props.heading) return props.heading;
  if (!isCreate.value) return 'Edit clause';
  return props.library === 'gaia' ? 'Add Gaia clause' : 'Add clause';
});

const primaryLabel = computed(() => (isCreate.value ? 'Create' : 'Save'));

/** The counter doubles as the placeholder hint while the field is still empty. */
const charLabel = computed(() =>
  form.content.trim()
    ? `${form.content.trim().length} characters`
    : 'Placeholders in square brackets stay editable per contract.'
);

const errors = computed<Partial<Record<TouchableField, string>>>(() => ({
  name: form.name.trim() ? undefined : 'A name is required.',
  contract_types: form.contract_types.length ? undefined : 'Pick at least one contract type.',
  jurisdiction: form.jurisdiction.trim() ? undefined : 'A jurisdiction is required.',
  language: form.language.trim() ? undefined : 'A language is required.',
  content: form.content.trim() ? undefined : 'The clause content is required.',
}));

const ready = computed(() => Object.values(errors.value).every(error => !error));

/** An error is shown only once its field has been visited and left wanting. */
function errorFor(field: TouchableField): string | undefined {
  return touched[field] ? errors.value[field] : undefined;
}

/** The organisation library picks from the org's types, the GAIA library from the templates. */
const contractTypeOptions = computed<SelectOption[]>(() =>
  props.library === 'gaia'
    ? Object.entries(contractTypeTemplateKeyMap).map(([value, label]) => ({ label, value }))
    : contractTypeStore.uidOptions
);

/**
 * A clause's types as this library's form values. A copy crosses libraries, so
 * a template key resolves to the org's copy of that template, and an org type
 * to its template key; a type with no counterpart is dropped.
 */
function contractTypeValues(
  types: readonly (ContractTypeRef | ContractTypeTemplateKey)[] | null | undefined
): string[] {
  return (types ?? []).flatMap(type => {
    if (props.library === 'gaia') return typeof type === 'string' ? type : (type.key ?? []);
    return typeof type === 'string' ? (contractTypeStore.byKey(type)?.uid ?? []) : type.uid;
  });
}

function clauseSource(clause: GaiaEditableClause): string | null {
  return 'source' in clause ? (clause.source ?? null) : null;
}

function toForm(clause: GaiaEditableClause): ClauseFormData {
  return {
    name: clause.name,
    jurisdiction: clause.jurisdiction,
    language: clause.language,
    contract_types: contractTypeValues(clause.contract_types),
    clause_category: clause.clause_category ?? ClauseCategory.OTHER,
    content: clause.content,
    source: clauseSource(clause),
  };
}

// Re-seed on every open rather than on prop change alone: the modal is reused
// for create and edit, so a stale form is a wrong save, not just a wrong view.
watch(
  () => [props.show, props.clause, props.prefill, props.mode] as const,
  () => {
    if (!props.show) return;
    const seed = isCreate.value ? props.prefill : props.clause;
    Object.assign(form, seed ? toForm(seed) : emptyForm());
    for (const field of Object.keys(touched) as TouchableField[]) touched[field] = false;
    saveError.value = null;
    // Template keys only resolve to org uids once the org's types are in.
    if (!contractTypeStore.loaded) {
      void contractTypeStore.ensureLoaded().then(() => {
        if (seed && !touched.contract_types) {
          form.contract_types = contractTypeValues(seed.contract_types);
        }
      });
    }
  },
  { immediate: true }
);

function close(): void {
  emit('update:show', false);
}

function writePayload(): ClauseCreateWrite {
  // An emptied Source field is "no source", not the empty string.
  const source = form.source?.trim() ?? '';
  const fields = {
    name: form.name.trim(),
    jurisdiction: form.jurisdiction.trim(),
    language: form.language.trim(),
    clause_category: form.clause_category,
    content: form.content,
    source: source.length > 0 ? source : null,
  };
  return props.library === 'gaia'
    ? {
        library: 'gaia',
        body: {
          ...fields,
          contract_types: form.contract_types.filter(key =>
            isEnumValue(ContractTypeTemplateKey, key)
          ),
        },
      }
    : { library: 'organisation', body: { ...fields, contract_type_uids: form.contract_types } };
}

async function save(): Promise<void> {
  if (!ready.value) {
    for (const field of Object.keys(touched) as TouchableField[]) touched[field] = true;
    return;
  }

  saving.value = true;
  saveError.value = null;
  try {
    const saved =
      isCreate.value || !props.clause
        ? await ClauseLibraryService.create(writePayload())
        : await ClauseLibraryService.update(props.clause.uid, writePayload());
    message.success(isCreate.value ? `${saved.name} created.` : `${saved.name} saved.`);
    emit('saved', saved);
    close();
  } catch (error) {
    logger.error('Failed to save clause', error);
    saveError.value = error instanceof Error ? error.message : 'Failed to save the clause.';
  } finally {
    saving.value = false;
  }
}

const serif = gaiaFonts.prose;
</script>

<template>
  <!-- Create/edit for either clause library. One form, because the two
       libraries share their columns; only the endpoint pair differs — which
       `ClauseLibraryService` owns — and what a contract type is (an org type
       or a template key), which `contractTypeOptions` covers. -->
  <gaia-modal
    :show="props.show"
    width="640px"
    closable
    :mask-closable="false"
    @update:show="(value: boolean) => emit('update:show', value)"
  >
    <template #header>
      <gaia-header :level="2" variant="display" size="dialog">{{ title }}</gaia-header>
    </template>

    <n-flex vertical :size="16" :wrap="false">
      <gaia-banner v-if="saveError" :title="saveError" closable @close="saveError = null" />

      <gaia-form-field label="Name" required :error="errorFor('name')">
        <gaia-input
          v-model:value="form.name"
          placeholder="Enter clause name"
          @blur="touched.name = true"
        />
      </gaia-form-field>

      <gaia-form-field label="Contract types" required :error="errorFor('contract_types')">
        <gaia-select
          v-model:value="form.contract_types"
          multiple
          filterable
          :max-tag-count="3"
          :options="contractTypeOptions"
          placeholder="Select contract types"
          @blur="touched.contract_types = true"
        />
      </gaia-form-field>

      <gaia-form-field label="Category" required>
        <gaia-enum-select
          v-model:value="form.clause_category"
          filterable
          :labels="clauseCategoryMap"
          placeholder="Select category"
        />
      </gaia-form-field>

      <n-grid :cols="2" :x-gap="12">
        <n-grid-item>
          <gaia-form-field label="Jurisdiction" required :error="errorFor('jurisdiction')">
            <gaia-input
              v-model:value="form.jurisdiction"
              placeholder="e.g. Germany, EU, UK"
              @blur="touched.jurisdiction = true"
            />
          </gaia-form-field>
        </n-grid-item>
        <n-grid-item>
          <gaia-form-field label="Language" required :error="errorFor('language')">
            <gaia-input
              v-model:value="form.language"
              placeholder="e.g. English, German"
              @blur="touched.language = true"
            />
          </gaia-form-field>
        </n-grid-item>
      </n-grid>

      <gaia-form-field label="Content" required :error="errorFor('content')">
        <n-flex vertical :size="6" :wrap="false" class="content-field">
          <gaia-textarea
            v-model:value="form.content"
            placeholder="Enter clause content. Use [Placeholders] for values that change per contract."
            :autosize="{ minRows: 7, maxRows: 18 }"
            @blur="touched.content = true"
          />
          <gaia-text size="micro" tone="faint">{{ charLabel }}</gaia-text>
        </n-flex>
      </gaia-form-field>

      <gaia-form-field label="Source" hint="Optional">
        <gaia-input
          v-model:value="form.source"
          placeholder="Where this wording comes from — a template, matter or precedent"
        />
      </gaia-form-field>
    </n-flex>

    <template #action>
      <n-flex justify="end" align="center" :size="10" :wrap="false">
        <gaia-button quaternary :disabled="saving" @click="close">Cancel</gaia-button>
        <gaia-button type="primary" :disabled="!ready" :loading="saving" @click="save">
          {{ primaryLabel }}
        </gaia-button>
      </n-flex>
    </template>
  </gaia-modal>
</template>

<style scoped>
/* The one place the app types into the serif: a clause is drafted here and read
   back in `GaiaClauseText`, and a mismatch between the two makes the preview
   look like a different clause. naive has no font-family var on n-input, so the
   textarea element has to be reached directly. */
.content-field :deep(textarea) {
  font-family: v-bind(serif);
  line-height: 1.7;
}
</style>
